import { useMemo, useState } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Skeleton,
  SimpleGrid,
  Pagination,
  Badge,
} from '@mantine/core';
import {
  IconCalendarTime,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconLock,
  IconLockOpen,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import { useFiscalPeriods } from '@/hooks/usePortalApi';

type SortField = 'period' | 'fiscal_year' | 'status';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function FiscalPeriods() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('fiscal_year');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data: periods, isLoading } = useFiscalPeriods();

  const allPeriods = periods ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allPeriods;
    return [...allPeriods].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'period') cmp = a.period.localeCompare(b.period);
      else if (sortField === 'fiscal_year') cmp = a.fiscal_year - b.fiscal_year || a.period_month - b.period_month;
      else cmp = a.status.localeCompare(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allPeriods, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pagePeriods = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCount = allPeriods.filter((p) => !p.is_locked).length;
  const closedCount = allPeriods.filter((p) => p.is_locked).length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader title="Fiscal Periods" description="View accounting fiscal period status" />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard title="Open Periods" value={String(openCount)} icon={<IconLockOpen size={24} />} />
        <StatCard title="Closed Periods" value={String(closedCount)} icon={<IconLock size={24} />} />
        <StatCard title="Total Periods" value={String(allPeriods.length)} icon={<IconCalendarTime size={24} />} />
      </SimpleGrid>

      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : pagePeriods.length === 0 ? (
        <EmptyState icon={<IconCalendarTime size={30} />} title="No fiscal periods" description="No fiscal periods have been configured yet" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="period">Period</ThSort>
                  <ThSort field="fiscal_year">Fiscal Year</ThSort>
                  <Table.Th>Month</Table.Th>
                  <ThSort field="status">Status</ThSort>
                  <Table.Th>Lock</Table.Th>
                  <Table.Th>Locked By</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagePeriods.map((period) => (
                  <Table.Tr key={period.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{period.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{period.period}</Text></Table.Td>
                    <Table.Td><Text size="sm">{period.fiscal_year}</Text></Table.Td>
                    <Table.Td><Text size="sm">{period.period_month}</Text></Table.Td>
                    <Table.Td><StatusBadge status={period.status} /></Table.Td>
                    <Table.Td>
                      <Badge
                        color={period.is_locked ? 'red' : 'green'}
                        variant="light"
                        leftSection={period.is_locked ? <IconLock size={12} /> : <IconLockOpen size={12} />}
                      >
                        {period.is_locked ? 'Locked' : 'Open'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{period.locked_by || '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}
    </Stack>
  );
}
